/**
 * Keyboard Shortcuts Help Modal
 * 
 * Displays available keyboard shortcuts to users
 * Requirements: 13.1
 */

import { useEffect, useState } from 'react';
import { Modal } from './Modal';
import { useAuth } from '../../auth/AuthProvider';

export function KeyboardShortcutsHelp() {
  const [isOpen, setIsOpen] = useState(false);
  const { isAuthenticated, isAdmin, isCoach } = useAuth();

  useEffect(() => {
    const handleShowShortcuts = () => {
      setIsOpen(true);
    };

    window.addEventListener('show-keyboard-shortcuts', handleShowShortcuts);

    return () => {
      window.removeEventListener('show-keyboard-shortcuts', handleShowShortcuts);
    };
  }, []);

  const shortcuts = [
    { key: 'Alt + D', description: 'Go to Dashboard', available: isAuthenticated },
    { key: 'Alt + E', description: 'Go to Events', available: isAuthenticated },
    { key: 'Alt + C', description: 'Go to Calendar', available: isAuthenticated },
    { key: 'Alt + L', description: 'Go to Leaderboard', available: isAuthenticated },
    { key: 'Alt + P', description: 'Go to Profile', available: isAuthenticated },
    { key: 'Alt + R', description: 'Go to Riders', available: isCoach || isAdmin },
    { key: 'Alt + U', description: 'Go to Users', available: isAdmin },
    { key: 'Shift + ?', description: 'Show this help', available: true },
    { key: 'Escape', description: 'Close modals', available: true },
    { key: 'Tab', description: 'Navigate between elements', available: true },
    { key: 'Shift + Tab', description: 'Navigate backwards', available: true },
    { key: 'Enter', description: 'Activate focused element', available: true },
    { key: 'Space', description: 'Activate buttons/checkboxes', available: true },
  ];

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => setIsOpen(false)}
      title="Keyboard Shortcuts"
      className="max-w-2xl"
    >
      <div className="space-y-4">
        <p className="text-gray-600">
          Use these keyboard shortcuts to navigate the application quickly.
        </p>

        <div className="space-y-2">
          {shortcuts
            .filter((shortcut) => shortcut.available)
            .map((shortcut) => (
              <div
                key={shortcut.key}
                className="flex items-center justify-between py-2 border-b border-gray-200 last:border-0"
              >
                <span className="text-gray-700">{shortcut.description}</span>
                <kbd className="px-3 py-1 text-sm font-semibold text-gray-800 bg-gray-100 border border-gray-300 rounded">
                  {shortcut.key}
                </kbd>
              </div>
            ))}
        </div>

        <div className="mt-6 p-4 bg-primary-50 rounded-lg">
          <p className="text-sm text-gray-700">
            <strong>Tip:</strong> Keyboard shortcuts won't work when typing in input fields.
            Press <kbd className="px-2 py-0.5 text-xs font-semibold text-gray-800 bg-white border border-gray-300 rounded">Escape</kbd> to exit input fields and use shortcuts.
          </p>
        </div>
      </div>
    </Modal>
  );
}
